// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: formulaire d'action — logique pure du load / diff / save
// ═══════════════════════════════════════════════════════════════════════════
//
// Décisions extraites de `modules/component-action-modal/index.ts` (le flux le
// plus utilisé et le plus risqué : 14 champs, 8 call-sites). Le monolithe garde
// la glu DOM (lire les inputs, écrire la textarea, afficher les confirmations) ;
// ici vit la SEULE logique testable, sans aucune dépendance DOM :
//
//   - computeDefaultStatusId : statut proposé par défaut à l'ouverture.
//   - buildOriginalSnapshot  : action backend → état initial (defaults, Sets).
//   - buildActionPayload     : valeurs du formulaire → payload POST/PUT.
//   - changedActionFields    : diff snapshot ↔ payload courant (libellés FR).
//   - requiredSaveConfirmations / shouldBlockForAutoNote : garde-fous du save.
//
// Invariant clé (testé) : recharger une action puis la resauvegarder SANS rien
// toucher ne doit produire AUCUN champ modifié (round-trip snapshot↔payload).
//
// ⚠️ Comportement reproduit À L'IDENTIQUE de l'existant (extraction, pas refonte) :
//   - les libellés de `changedActionFields` sont en dur (non i18n), comme avant.
// Cette dette est connue, à traiter séparément si décidé.
//
// Août 2026 : la priorité passe par `normalizePriority` (ci-dessous) au lieu
// d'un parseInt brut — elle ne peut plus valoir NaN ni sortir du barème 0..4.

// ── Types d'entrée / sortie ────────────────────────────────────────────────

/** Statut d'action côté référentiel (forme minimale utile ici). */
export interface ActionStatusOption {
  id: number;
  position: number;
  is_system?: boolean;
}

/** Action telle que reçue du backend pour édition (champs optionnels). */
export interface ActionEditData {
  id?: number;
  text?: string;
  deadline?: string | null;
  deadline_revised?: string | null;
  status_id?: number | null;
  progress?: number;
  priority?: number | null;
  expected_deliverable?: string | null;
  external_link?: string | null;
  approver_id?: number | null;
  performers?: number[];
  consulted?: number[];
  informed?: number[];
  project_ids?: number[];
  committee_ids?: number[];
  structure_ids?: number[];
  escalated_structure_ids?: number[];
  phase_ids?: number[];
  is_confidential?: boolean;
  owner_id?: number | null;
  // Calculé backend : l'user courant peut-il basculer la confidentialité ?
  can_toggle_confidential?: boolean;
  origins?: Array<{
    project_code?: string | null;
    project_name?: string | null;
    risk_id?: number | null;
    risk_code?: string | null;
    risk_category?: string | null;
    risk_consequence?: string | null;
    severity?: number | null;
  }>;
}

/**
 * Snapshot de l'état initial du formulaire, capturé à l'ouverture. Les
 * rattachements sont des Set (comparaison d'appartenance lors du diff).
 */
export interface ActionSnapshot {
  text: string;
  deadline: string;
  deadline_revised: string;
  priority: number;
  expected_deliverable: string;
  external_link: string;
  status_id: number | null;
  progress: number;
  approver_id: number | null;
  performers: Set<number>;
  consulted: Set<number>;
  informed: Set<number>;
  project_ids: Set<number>;
  committee_ids: Set<number>;
  structure_ids: Set<number>;
  escalated_structure_ids: Set<number>;
  phase_ids: Set<number>;
  is_confidential: boolean;
  owner_id: number | null;
}

/**
 * Valeurs brutes lues dans le formulaire au moment du save. Les champs
 * `*Value` sont les valeurs textuelles des inputs/selects (parsées ici, pas
 * dans le DOM) ; les ids sont déjà collectés par le caller (chips RACI, Sets
 * vivants des pickers projets/structures, comités cochés).
 */
export interface ActionFormRawInput {
  text: string;
  deadlineValue: string;
  deadlineRevisedValue: string;
  statusValue: string;
  progressValue: string;
  priorityValue: string;
  expectedDeliverableValue: string;
  externalLinkValue: string;
  approverValue: string;
  performers: number[];
  consulted: number[];
  informed: number[];
  projectIds: number[];
  committeeIds: number[];
  structureIds: number[];
  /** Ids escaladés sélectionnés (avant restriction aux structures liées). */
  pickedEscalatedIds: number[];
  /** Phases de plan d'action cochées (M2M, sans escalade). */
  phaseIds: number[];
  /** Owner choisi (valeur du select caché). Optionnel : '' / absent = pas d'owner. */
  ownerValue?: string;
  /** Case « Confidentiel » cochée. Optionnel pour rétro-compat des fixtures. */
  confidentialChecked?: boolean;
}

/** Payload envoyé en POST (création) / PUT (édition) au backend. */
export interface ActionSavePayload {
  text: string;
  deadline: string | null;
  deadline_revised: string | null;
  status_id: number | null;
  progress: number;
  priority: number;
  expected_deliverable: string | null;
  external_link: string | null;
  approver_id: number | null;
  performers: number[];
  consulted: number[];
  informed: number[];
  project_ids: number[];
  committee_ids: number[];
  structure_ids: number[];
  escalated_structure_ids: number[];
  phase_ids: number[];
  is_confidential: boolean;
  owner_id: number | null;
}

// ── Statut par défaut ──────────────────────────────────────────────────────

/**
 * Statut proposé par défaut à l'ouverture : le premier statut NON système
 * (les statuts système comme « Fusionnée » sont posés par l'application et ne
 * doivent jamais être assignés manuellement), trié par `position`. `null` si
 * aucun statut assignable.
 */
export function computeDefaultStatusId(statuses: ActionStatusOption[]): number | null {
  const sorted = [...statuses]
    .filter((s) => s.is_system !== true)
    .sort((x, y) => x.position - y.position);
  return sorted.length > 0 ? (sorted[0]?.id ?? null) : null;
}

// ── Snapshot initial ───────────────────────────────────────────────────────

/** Priorité retenue quand rien d'exploitable n'est fourni : 2 – Normale. */
export const ACTION_PRIORITY_DEFAULT = 2;

/**
 * Replie une priorité sur le barème 0 (Très haute) → 4 (Très basse).
 *
 * Tout ce qui n'appartient pas au barème — `null`, absent, chaîne vide, valeur
 * hors bornes — retombe sur 2 (Normale), le défaut de la colonne côté base.
 * Sans ce garde-fou, une priorité hors barème (le seed de démo en produisait à
 * 5) laissait le `<select>` sans option `selected` : le navigateur affichait
 * alors la première, « 0 – Très haute », alors que la liste n'affichait rien.
 */
export function normalizePriority(raw: number | string | null | undefined): number {
  const n = typeof raw === 'string' ? parseInt(raw, 10) : raw;
  if (typeof n !== 'number' || !Number.isInteger(n) || n < 0 || n > 4) {
    return ACTION_PRIORITY_DEFAULT;
  }
  return n;
}

/**
 * Construit l'état initial du formulaire depuis l'action backend, en
 * appliquant les valeurs par défaut : priorité 2 (Normale) si absente ou hors
 * barème, statut effectif = statut de l'action sinon `defaultStatusId`,
 * avancement 0.
 */
export function buildOriginalSnapshot(
  a: ActionEditData,
  defaultStatusId: number | null,
): ActionSnapshot {
  return {
    text: a.text || '',
    deadline: a.deadline || '',
    deadline_revised: a.deadline_revised || '',
    priority: normalizePriority(a.priority),
    expected_deliverable: a.expected_deliverable || '',
    external_link: a.external_link || '',
    status_id: a.status_id ?? defaultStatusId,
    progress: a.progress || 0,
    approver_id: a.approver_id ?? null,
    performers: new Set<number>(a.performers || []),
    consulted: new Set<number>(a.consulted || []),
    informed: new Set<number>(a.informed || []),
    project_ids: new Set<number>(a.project_ids || []),
    committee_ids: new Set<number>(a.committee_ids || []),
    structure_ids: new Set<number>(a.structure_ids || []),
    escalated_structure_ids: new Set<number>(a.escalated_structure_ids || []),
    phase_ids: new Set<number>(a.phase_ids || []),
    is_confidential: a.is_confidential ?? false,
    owner_id: a.owner_id ?? null,
  };
}

// ── Payload de sauvegarde ──────────────────────────────────────────────────

// parseInt(raw) puis bascule sur `fallback` pour 0/NaN — reproduit le motif
// historique `parseInt(el?.value || '') || fallback` (les ids backend ne valant
// jamais 0, le `|| null` ne masque rien d'utile).
function intOr(raw: string, fallback: number | null): number | null {
  return parseInt(raw, 10) || fallback;
}

/**
 * Assemble le payload de sauvegarde depuis les valeurs brutes du formulaire.
 * `escalated_structure_ids` est restreint aux structures effectivement liées
 * (invariant : escaladé ⊆ structures liées).
 */
export function buildActionPayload(input: ActionFormRawInput): ActionSavePayload {
  const escalated = new Set<number>(input.pickedEscalatedIds);
  return {
    text: input.text,
    deadline: input.deadlineValue || null,
    deadline_revised: input.deadlineRevisedValue || null,
    status_id: intOr(input.statusValue, null),
    progress: intOr(input.progressValue, 0) ?? 0,
    priority: normalizePriority(input.priorityValue),
    expected_deliverable: input.expectedDeliverableValue.trim() || null,
    external_link: input.externalLinkValue.trim() || null,
    approver_id: intOr(input.approverValue, null),
    performers: input.performers,
    consulted: input.consulted,
    informed: input.informed,
    project_ids: input.projectIds,
    committee_ids: input.committeeIds,
    structure_ids: input.structureIds,
    escalated_structure_ids: input.structureIds.filter((id) => escalated.has(id)),
    phase_ids: input.phaseIds,
    is_confidential: input.confidentialChecked ?? false,
    owner_id: intOr(input.ownerValue ?? '', null),
  };
}

// ── Diff snapshot ↔ état courant ───────────────────────────────────────────

// Un Set d'origine diffère d'une liste courante si tailles ≠ ou un id absent.
function arrSetDiffers(orig: Set<number>, now: number[]): boolean {
  if (orig.size !== now.length) return true;
  for (const n of now) if (!orig.has(n)) return true;
  return false;
}

/**
 * Liste des champs modifiés entre le snapshot initial et le payload courant,
 * sous forme de libellés FR prêts pour l'auto-note (« Modification <liste> »).
 * Compare le payload reconstruit (et non le DOM) pour rester pur et cohérent
 * avec ce qui sera réellement sauvegardé.
 */
export function changedActionFields(snap: ActionSnapshot, cur: ActionSavePayload): string[] {
  const labels: string[] = [];
  if (cur.text !== snap.text) labels.push('du titre');
  if ((cur.deadline || '') !== snap.deadline) labels.push('de la date butoir initiale');
  if ((cur.deadline_revised || '') !== snap.deadline_revised)
    labels.push('de la date de fin révisée');
  if (cur.status_id !== snap.status_id) labels.push("de l'état");
  if (cur.progress !== snap.progress) labels.push("de l'avancement");
  // Priorité non comparée si NaN (select vide) — fidèle au garde existant.
  if (!Number.isNaN(cur.priority) && cur.priority !== snap.priority) labels.push('de la priorité');
  if ((cur.expected_deliverable || '') !== snap.expected_deliverable)
    labels.push('du livrable attendu');
  if ((cur.external_link || '') !== snap.external_link) labels.push('du lien vers un autre outil');
  if (cur.approver_id !== snap.approver_id) labels.push("de l'approbateur (A)");
  if (cur.owner_id !== snap.owner_id) labels.push("de l'owner");
  if (cur.is_confidential !== snap.is_confidential) labels.push('de la confidentialité');
  if (arrSetDiffers(snap.performers, cur.performers)) labels.push('des réalisateurs (R)');
  if (arrSetDiffers(snap.consulted, cur.consulted)) labels.push('des consultés (C)');
  if (arrSetDiffers(snap.informed, cur.informed)) labels.push('des informés (I)');
  if (arrSetDiffers(snap.project_ids, cur.project_ids)) labels.push('de la liste des projets');
  if (arrSetDiffers(snap.committee_ids, cur.committee_ids)) labels.push('de la liste des comités');
  if (arrSetDiffers(snap.structure_ids, cur.structure_ids)) labels.push("de l'organisation");
  if (arrSetDiffers(snap.escalated_structure_ids, cur.escalated_structure_ids))
    labels.push("de l'escalade d'organisation");
  if (arrSetDiffers(snap.phase_ids, cur.phase_ids)) labels.push("des plans d'action");
  return labels;
}

// ── Garde-fous du save ─────────────────────────────────────────────────────

/** Confirmation utilisateur exigée avant d'enregistrer. */
export type ActionSaveConfirm = 'noProject' | 'deadlineChange';

export interface SaveConfirmContext {
  isNew: boolean;
  projectIdsCount: number;
  oldDeadline: string | null;
  newDeadline: string | null;
}

/**
 * Confirmations à demander avant le save, dans l'ordre. Au plus une (les deux
 * cas s'excluent par `isNew`) :
 *   - `noProject`      : création sans aucun projet lié (action orpheline).
 *   - `deadlineChange` : édition modifiant la date butoir INITIALE (préférer
 *     la date de fin révisée).
 */
export function requiredSaveConfirmations(ctx: SaveConfirmContext): ActionSaveConfirm[] {
  const out: ActionSaveConfirm[] = [];
  if (ctx.isNew && ctx.projectIdsCount === 0) out.push('noProject');
  if (!ctx.isNew && ctx.oldDeadline && ctx.newDeadline !== ctx.oldDeadline)
    out.push('deadlineChange');
  return out;
}

export interface AutoNoteGuardContext {
  autoNoteEnabled: boolean;
  userEditedNote: boolean;
  autoNoteWarned: boolean;
  autoNoteText: string;
}

/**
 * Vrai si le 1er clic « Enregistrer » doit être bloqué pour attirer l'attention
 * sur l'auto-note non relue : auto-note active, non éditée par l'utilisateur,
 * pas encore avertie, et textarea non vide. Le 2e clic (warned) passe.
 */
export function shouldBlockForAutoNote(ctx: AutoNoteGuardContext): boolean {
  return (
    ctx.autoNoteEnabled &&
    !ctx.userEditedNote &&
    !ctx.autoNoteWarned &&
    ctx.autoNoteText.trim().length > 0
  );
}
